import { useState } from 'react';
import { FolderIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { Button, Card, Badge, Spinner, Modal, Input } from '@realvisor/ui';
import { useProjects } from '@/hooks';
import { useProject } from '@/contexts/ProjectContext';
import { api } from '@/lib/api';

// Generate consistent color from string
function stringToColor(str: string): string {
  const colors = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length] ?? '#6B7280';
}

export default function Projects() {
  const queryClient = useQueryClient();
  const { data: projects, isLoading, error } = useProjects();
  const { refreshProjects } = useProject();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string; description?: string }) => 
      api.post('/projects', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      refreshProjects();
      setIsCreateModalOpen(false);
      setFormData({ name: '', description: '' });
      toast.success('Projekt byl vytvořen');
    },
    onError: () => {
      toast.error('Nepodařilo se vytvořit projekt');
    },
  });

  const handleCreate = () => {
    if (!formData.name.trim()) {
      toast.error('Zadejte název projektu');
      return;
    }
    createMutation.mutate({
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Projekty</h1>
          <p className="text-sm text-gray-500">
            {projects ? `${projects.length} projektů` : 'Správa projektů a jejich nastavení'}
          </p>
        </div>
        <Button leftIcon={<PlusIcon className="h-5 w-5" />} onClick={() => setIsCreateModalOpen(true)}>
          Nový projekt
        </Button>
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Nový projekt"
      >
        <div className="space-y-4">
          <Input
            label="Název projektu"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Např. Prodej bytů Praha"
            autoFocus
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Popis (volitelné)</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Krátký popis projektu..."
              rows={3}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>
              Zrušit
            </Button>
            <Button onClick={handleCreate} isLoading={createMutation.isPending}>
              Vytvořit projekt
            </Button>
          </div>
        </div>
      </Modal>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      )}

      {/* Error state */}
      {error && (
        <Card padding="lg">
          <div className="text-center text-red-500">
            Chyba při načítání projektů
          </div>
        </Card>
      )}

      {/* Empty state */}
      {!isLoading && !error && projects?.length === 0 && (
        <Card padding="lg">
          <div className="text-center py-8">
            <FolderIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Žádné projekty</h3>
            <p className="mt-1 text-sm text-gray-500">Začněte vytvořením prvního projektu.</p>
            <div className="mt-6">
              <Button leftIcon={<PlusIcon className="h-5 w-5" />} onClick={() => setIsCreateModalOpen(true)}>
                Nový projekt
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Projects grid */}
      {!isLoading && !error && projects && projects.length > 0 && (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => {
            const color = stringToColor(project.name);
            return (
              <Link key={project.id} to={`/projects/${project.id}`}>
                <Card className="h-full hover:shadow-md transition-shadow">
                  <div className="p-6">
                    <div className="flex items-start gap-4">
                      <div
                        className="flex h-12 w-12 items-center justify-center rounded-lg"
                        style={{ backgroundColor: `${color}20` }}
                      >
                        <FolderIcon className="h-6 w-6" style={{ color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900">{project.name}</h3>
                        <p className="text-sm text-gray-500">{project.slug}</p>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-sm text-gray-500">
                        {project._count?.leads || 0} leadů
                      </span>
                      <Badge variant={project.archivedAt ? 'default' : 'success'}>
                        {project.archivedAt ? 'Archivován' : 'Aktivní'}
                      </Badge>
                    </div>
                    {project.description && (
                      <p className="mt-3 text-sm text-gray-500 line-clamp-2">
                        {project.description}
                      </p>
                    )}
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
